import * as React from "react";
import { Chip } from "@mui/material";

import type { RequestState } from "@/hooks/api/use-campaign-requests";

import { REQUEST_STATE_CLIENT_LABELS, REQUEST_STATE_LABELS } from "../constants";
import { labelOf } from "../humanise";
import { HelpTooltip } from "./help-tooltip";

type ChipColor = "primary" | "secondary" | "info" | "success" | "warning" | "error";
type ChipWeight = "outlined" | "soft" | "filled";

interface StateAppearance {
	color: ChipColor;
	weight: ChipWeight;
}

/**
 * How each of the eleven request states is drawn.
 *
 * Two axes, and both of them mean something on their own — which is the whole point, because a
 * badge is read in peripheral vision while scanning a column, not studied. Before this, `draft`
 * and `completed` were both `default`, so the two ends of the lifecycle were the same grey
 * outline and told a scanning eye nothing.
 *
 * **Hue is the phase of the request.** It answers "roughly where is this?" without reading:
 *
 * | hue         | phase                                                    |
 * | ----------- | -------------------------------------------------------- |
 * | `secondary` | grey — nothing is moving. Not sent yet, or being built.  |
 * | `info`      | blue — with us, commercials open.                        |
 * | `warning`   | amber — the client has the ball.                          |
 * | `primary`   | violet — agreed and heading for the air.                  |
 * | `success`   | green — live, or finished well.                           |
 * | `error`     | red — finished badly.                                     |
 *
 * **Weight is how much attention it wants**, and it is what keeps two states sharing a hue apart:
 *
 * - `filled` — act now, or it is live and metering. Three states only, so a filled chip in a list
 *   is genuinely rare and genuinely means something.
 * - `soft` — in flight. Someone is working, nobody is waiting on a decision.
 * - `outlined` — static. Not started, or over. A hairline reads as "nothing is happening here",
 *   which is exactly true of `draft`, `completed` and `cancelled`.
 *
 * Every one of the eleven is a unique (hue, weight) pair, so no two states can ever collide. The
 * pairs that matter most:
 *
 * - `draft` grey hairline vs `completed` green hairline — the two ends, now different hues.
 * - `quoted` solid blue vs `submitted` blue wash — a price exists vs it does not.
 * - `awaiting_confirmation` solid amber vs `changes_requested` amber wash — a gate vs a nudge.
 * - `ready` solid violet vs `confirmed` violet wash — cleared to dial vs merely agreed.
 *
 * Contrast on the dark ground (`background.paper` #121517), all AA or better at body size:
 * filled chips are the `400` shade with black label — info 8.3:1, warning 11:1, primary 6.1:1,
 * success 10.9:1. Soft chips are the `800` shade with the `200` label — info 6.2:1, warning
 * 5.7:1, primary 9.8:1, secondary 8.9:1. Outlined chips draw the label at `400` on paper —
 * success 9.5:1, error 6.6:1 — except `secondary`, which the theme corrects to `secondary-50`.
 *
 * The scale is the same on client and admin surfaces. Only the words change: the client reads
 * "Awaiting your confirmation" where we read "Awaiting confirmation", and a badge that changed
 * colour as well as wording between the two screens would make them impossible to talk about on a
 * support call.
 */
export const REQUEST_STATE_APPEARANCE: Record<RequestState, StateAppearance> = {
	draft: { color: "secondary", weight: "outlined" },
	submitted: { color: "info", weight: "soft" },
	quoted: { color: "info", weight: "filled" },
	awaiting_confirmation: { color: "warning", weight: "filled" },
	changes_requested: { color: "warning", weight: "soft" },
	confirmed: { color: "primary", weight: "soft" },
	in_preparation: { color: "secondary", weight: "soft" },
	ready: { color: "primary", weight: "filled" },
	running: { color: "success", weight: "filled" },
	completed: { color: "success", weight: "outlined" },
	cancelled: { color: "error", weight: "outlined" },
};

/** The fallback for a state the API grew and this build has not learned yet. */
const UNKNOWN_STATE: StateAppearance = { color: "secondary", weight: "outlined" };

export interface StateChipProps {
	state: RequestState;
	/**
	 * Client surfaces read the request's own language — "Awaiting your confirmation", not
	 * `awaiting_confirmation`. Admin surfaces read the state name, because that is what the queue
	 * and the transition buttons talk about.
	 */
	variant?: "client" | "admin";
	size?: "small" | "medium";
}

/**
 * One request state, everywhere it appears.
 *
 * On client surfaces the state name is kept on hover rather than dropped: support answers
 * questions in the client's words and reads the log in ours, and the two have to be joinable. A
 * chip is already at its full width, so there is nowhere to put a second label — this is one of
 * the few places a hover tooltip earns its keep. It only opens when the two labels actually
 * differ; for `draft`, `confirmed`, `running`, `completed` and `cancelled` they are the same
 * word, and a tooltip that repeats the thing you are pointing at is noise.
 */
export function StateChip({ state, variant = "admin", size = "small" }: StateChipProps): React.JSX.Element {
	const { color, weight } = REQUEST_STATE_APPEARANCE[state] ?? UNKNOWN_STATE;
	const adminLabel = labelOf(REQUEST_STATE_LABELS, state);
	const label = variant === "client" ? labelOf(REQUEST_STATE_CLIENT_LABELS, state) : adminLabel;

	const chip = <Chip color={color} label={label} size={size} variant={weight} />;

	if (variant !== "client" || label === adminLabel) return chip;

	return (
		<HelpTooltip title={adminLabel} describeChild={false}>
			{chip}
		</HelpTooltip>
	);
}
